import { AxisCode } from '@atlas/types'
import type { CampaignListItem, CampaignListTag, CampaignListVariant } from '@atlas/ui-domain'
import { CAMPAIGN_STATUS_VARIANT } from '@/components/campaigns/CampaignStatusBadge'
import { CAMPAIGN_STATUS_LABELS, type Campaign } from '@/domain/campaign.types'

/**
 * The axes shown as tags beside a campaign, in the order they read - what the campaign is for,
 * then where it runs - each with the weight it carries. Which axes those are is taxonomy
 * knowledge, so it lives here rather than in the card.
 */
const TAG_AXES: { axisCode: string; variant: CampaignListVariant }[] = [
	{ axisCode: AxisCode.OBJECTIVE, variant: 'secondary' },
	{ axisCode: AxisCode.GEOGRAPHY, variant: 'muted' },
]

/**
 * A hydrated Campaign as `CampaignListCard` states it.
 *
 * The lifecycle enum is resolved here into a label and a variant, through the same map
 * `CampaignStatusBadge` uses, so the campaigns table and a list card cannot disagree about what
 * a state looks like. The tags are read off the resolved classification, so the card never sees
 * an axis code. An axis the campaign carries no tag on contributes no tag at all.
 */
export const toCampaignListItem = (campaign: Campaign): CampaignListItem => ({
	id: campaign.campaignId,
	name: campaign.name,
	code: campaign.campaignCode,
	windowLabel: campaign.windowLabel,
	tags: TAG_AXES.flatMap(({ axisCode, variant }): CampaignListTag[] => {
		const tag = campaign.classification.tags.find((t) => t.axisCode === axisCode)
		return tag ? [{ label: tag.label, variant }] : []
	}),
	status: { label: CAMPAIGN_STATUS_LABELS[campaign.status], variant: CAMPAIGN_STATUS_VARIANT[campaign.status] },
})

export const toCampaignListItems = (campaigns: Campaign[]): CampaignListItem[] => campaigns.map(toCampaignListItem)
